import React, { useEffect, useRef, useState } from "react";
import { ChevronDownIcon, FolderIcon } from "@heroicons/react/24/outline";
import { useCollections } from "@/features/documents/hooks";

interface ScopePickerProps {
  /** Selected collection ids; empty = org-wide ("All documents"). */
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  disabled?: boolean;
}

/**
 * Multi-select collection picker controlling the chat retrieval scope (#160).
 * The selection is sent with every query, so what the picker shows is always
 * what the next answer will be scoped to.
 */
const ScopePicker: React.FC<ScopePickerProps> = ({
  selectedIds,
  onChange,
  disabled,
}) => {
  const { collections } = useCollections();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    window.addEventListener("mousedown", onPointerDown);
    return () => window.removeEventListener("mousedown", onPointerDown);
  }, [open]);

  // Selected ids can reference collections deleted since selection (the
  // server hard-fails those) — render only ones that still resolve.
  const selectedNames = collections
    .filter((c) => selectedIds.includes(c.id))
    .map((c) => c.name);

  const toggle = (id: string) => {
    onChange(
      selectedIds.includes(id)
        ? selectedIds.filter((s) => s !== id)
        : [...selectedIds, id],
    );
  };

  const label =
    selectedNames.length === 0
      ? "All documents"
      : selectedNames.length <= 2
        ? selectedNames.join(", ")
        : `${selectedNames.length} collections`;

  if (collections.length === 0) return null;

  return (
    <div ref={rootRef} className="relative inline-block">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        disabled={disabled}
        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-text-muted bg-surface border border-subtle rounded-lg hover:bg-surface-2 transition-colors disabled:opacity-50"
        title="Restrict answers to specific collections"
      >
        <FolderIcon className="h-3.5 w-3.5" />
        <span className="max-w-[220px] truncate">{label}</span>
        <ChevronDownIcon className="h-3 w-3" />
      </button>

      {open && (
        <div className="absolute bottom-full mb-1 left-0 z-20 w-64 max-h-64 overflow-y-auto bg-surface border border-subtle rounded-lg shadow-lg p-1">
          <button
            type="button"
            onClick={() => {
              onChange([]);
              setOpen(false);
            }}
            className={`w-full text-left px-2.5 py-1.5 rounded text-xs transition-colors ${
              selectedIds.length === 0
                ? "bg-accent-tint text-accent font-medium"
                : "text-text-muted hover:bg-surface-2"
            }`}
          >
            All documents
          </button>
          <div className="my-1 border-t border-subtle" />
          {collections.map((c) => (
            <label
              key={c.id}
              className="flex items-center gap-2 px-2.5 py-1.5 rounded text-xs text-text-muted hover:bg-surface-2 cursor-pointer"
            >
              <input
                type="checkbox"
                checked={selectedIds.includes(c.id)}
                onChange={() => toggle(c.id)}
                className="h-3.5 w-3.5 rounded border-subtle text-accent focus:ring-accent"
              />
              <span className="truncate">{c.name}</span>
              <span className="ml-auto text-text-faint">{c.documentCount}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
};

export default ScopePicker;
